/**
 * UI-side case log — collected clues and the suspect roster derived from
 * clue payloads. No event carries SuspectDef[], so suspect display names are
 * prettified from `ClueDef.suspectId` (recorded as a contract deviation).
 * Owned by UI.
 */
import type { ClueDef, EventBus } from '../core/types'

export interface SuspectEntry {
  readonly id: string
  readonly name: string
}

export interface CaseLog {
  clues(): readonly ClueDef[]
  suspects(): readonly SuspectEntry[]
  suspectName(id: string): string
  subscribe(listener: () => void): void
}

/** 'vivian-marlowe' → 'Vivian Marlowe'. */
const prettifyId = (id: string): string =>
  id
    .split(/[-_\s]+/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

export const createCaseLog = (bus: EventBus): CaseLog => {
  let clues: readonly ClueDef[] = []
  let suspects: readonly SuspectEntry[] = []
  let listeners: readonly (() => void)[] = []

  const notify = (): void => {
    for (const listener of listeners) listener()
  }

  bus.on('clue:collected', ({ clue }) => {
    if (clues.some((known) => known.id === clue.id)) return
    clues = [...clues, clue]
    const suspectId = clue.suspectId
    if (suspectId !== undefined && !suspects.some((known) => known.id === suspectId)) {
      suspects = [...suspects, { id: suspectId, name: prettifyId(suspectId) }]
    }
    notify()
  })

  return {
    clues: () => clues,
    suspects: () => suspects,
    suspectName: (id) => suspects.find((known) => known.id === id)?.name ?? prettifyId(id),
    subscribe: (listener) => {
      listeners = [...listeners, listener]
    }
  }
}
